import { Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { concatMap } from 'rxjs/operators';
import { AIASSIST_FUNCTIONS_NAMESPACE } from '../interfaces/imetadata';
import { ApplicationStoreService } from './application-store.service';
import { ContentControlToFieldMapService, SerializedFieldContext } from './content-control-to-field-map.service';
import { ContentControlMonitor } from './document-monitors/content-control-monitor';
import { AiAssistFunctionProviderService } from './functions/aiassist.function-provider.service';
import { BullhornFunctionProviderService } from './functions/bullhorn.function-provider.service';
import { IFunction } from './functions/ifunction';
import { LoggingService } from './logging.service';
import { BullhornNamespace } from './service.provider.service';

/**
 * The FunctionsModelService maintains the "functions" part in the NamespaceSettings stored in the Web Extension Part
 * and used by the server to execute functions that put results into the final data model.
 */
@Injectable({ providedIn: 'root' })
export class FunctionsModelService {
  constructor(
    private applicationStoreService: ApplicationStoreService,
    private aiAssistFunctionProvider: AiAssistFunctionProviderService,
    private bullhornFunctionProvider: BullhornFunctionProviderService,
    private contentControlMonitor: ContentControlMonitor,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private loggingService: LoggingService
  ) {
    this.subscribeToContentControlDeletedEvents();
  }

  async insert(functionNamespace: string, namespace: Namespace | string, functionConfig: any): Promise<string> {
    // // TODO: really if it is a 'function' field
    // if (!fieldContext.field.path.startsWith('functions'))
    //   return undefined;

    // TODO: can we avoid this? Does it matter?
    const nss = this.applicationStoreService.getNamespaceSettings(namespace)!;
    nss.functions = nss.functions ?? [];

    let func = undefined;
    if (functionNamespace === BullhornNamespace.name) {
      func = await this.bullhornFunctionProvider.build(functionConfig);
    } else if (functionNamespace === AIASSIST_FUNCTIONS_NAMESPACE) {
      func = await this.aiAssistFunctionProvider.build(functionConfig);
    } else {
      throw new Error(`No function provider for '${functionNamespace}'.`);
    }

    this.loggingService.log('FunctionsModelService', 'insert', `Inserting function for ${functionNamespace} with id '${func.id}'.`);
    nss.functions.push(func);

    await this.applicationStoreService.setNamespaceSettings(namespace, nss);

    return func.id;
  }

  async delete(namespace: Namespace | string, id: string | undefined): Promise<IFunction | undefined> {
    if (!id) {
      this.loggingService.warn('FunctionsModelService', 'delete', `Undefined function id passed to delete.`);
      return undefined;
    }

    const nss = this.applicationStoreService.getNamespaceSettings(namespace);
    const functions = nss?.functions;
    if (!functions) {
      return undefined;
    }

    const idx = functions.findIndex((func) => func.id === id);
    if (idx < 0) {
      this.loggingService.error('FunctionsModelService', 'delete', `Function '${id}' not found.`);
      return undefined;
    }

    this.loggingService.log('FunctionsModelService', 'delete', `Deleting function with id '${id}'.`);
    const deletedFunc = functions.splice(idx, 1)[0];

    await this.applicationStoreService.setNamespaceSettings(namespace, nss);

    return deletedFunc;
  }

  find(namespace: Namespace | string, id: string): IFunction | undefined {
    const nss = this.applicationStoreService.getNamespaceSettings(namespace);
    const functions = nss?.functions;
    if (!functions) {
      return undefined;
    }

    const idx = functions.findIndex((func) => func.id === id);
    return idx < 0 ? undefined : functions[idx];
  }

  private subscribeToContentControlDeletedEvents(): void {
    this.contentControlMonitor.contentControlsDeleted$
      .pipe(concatMap((event) => this.contentControlDeleted({ id: event.id, tagRefId: event.tag })))
      .subscribe();
  }

  private async contentControlDeleted(tagValue: { id: number; tagRefId: string }): Promise<void> {
    const fieldContext = this.contentControlToFieldMapService.get(tagValue.id);
    if (!fieldContext) {
      // probably not one of our content controls
      return;
    }

    let functionId = null;
    if (fieldContext.imageFieldConfig?.functionId) {
      functionId = fieldContext.imageFieldConfig.functionId;
    } else if (fieldContext.aiAssistFieldConfig?.functionId) {
      functionId = fieldContext.aiAssistFieldConfig.functionId;
    }

    if (functionId) {
      await this.delete(fieldContext.namespace, functionId);
    }
  }
}
